import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Divider,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import LegalBusinessDetails from './LegalBusinessDetails';

const LOGO_SRC = '/struxor.png';
const PRIMARY = '#0087c1';

const NAV_LINKS = [
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Guide', to: '/guide' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const FREE_TOOL_LINKS = [
  { label: 'Quote Template', to: '/tools/quote-template' },
  { label: 'Purchase Order Template', to: '/tools/purchase-order-template' },
  { label: 'Invoice Template', to: '/tools/invoice-template' },
];

/**
 * Shared shell for all public marketing pages.
 * Provides a sticky top nav bar and a consistent footer.
 */
export default function PublicPageShell({ children }) {
  const theme = useTheme();
  const location = useLocation();
  const [freeToolsAnchor, setFreeToolsAnchor] = useState(null);
  const isFreeToolsActive = FREE_TOOL_LINKS.some((link) => location.pathname === link.to);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Nav */}
      <Box
        component="header"
        sx={{
          background: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: { xs: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Stack direction="row" spacing={{ xs: 1, md: 3 }} alignItems="center">
          <RouterLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <Box component="img" src={LOGO_SRC} alt="Struxor construction software" sx={{ height: 28 }} />
          </RouterLink>
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.to}
                component={RouterLink}
                to={link.to}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: location.pathname === link.to ? 700 : 500,
                  color: location.pathname === link.to ? PRIMARY : 'text.secondary',
                  borderBottom: location.pathname === link.to ? `2px solid ${PRIMARY}` : '2px solid transparent',
                  borderRadius: 0,
                  px: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {link.label}
              </Button>
            ))}
            <Button
              size="small"
              onClick={(event) => setFreeToolsAnchor(event.currentTarget)}
              sx={{
                textTransform: 'none',
                fontWeight: isFreeToolsActive ? 700 : 500,
                color: isFreeToolsActive ? PRIMARY : 'text.secondary',
                borderBottom: isFreeToolsActive ? `2px solid ${PRIMARY}` : '2px solid transparent',
                borderRadius: 0,
                px: 1.5,
                fontSize: '0.875rem',
              }}
            >
              Resources
            </Button>
            <Menu
              anchorEl={freeToolsAnchor}
              open={Boolean(freeToolsAnchor)}
              onClose={() => setFreeToolsAnchor(null)}
            >
              {FREE_TOOL_LINKS.map((link) => (
                <MenuItem
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  selected={location.pathname === link.to}
                  onClick={() => setFreeToolsAnchor(null)}
                >
                  {link.label}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            component={RouterLink}
            to="/login"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}
          >
            Log In
          </Button>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="small"
            sx={{
              background: PRIMARY,
              '&:hover': { background: '#006fa0' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              fontSize: '0.875rem',
            }}
          >
            Start Free Trial
          </Button>
        </Stack>
      </Box>

      {/* Page content */}
      <Box component="main" sx={{ flex: 1 }}>
        {children}
      </Box>

      {/* Footer */}
      <Divider />
      <Container sx={{ py: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          justifyContent="space-between"
          spacing={3}
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
        >
          <Stack spacing={1.5} sx={{ maxWidth: 760 }}>
            <Stack direction="row" alignItems="center" spacing={1} justifyContent={{ xs: 'center', md: 'flex-start' }}>
              <Box component="img" src={LOGO_SRC} alt="Struxor" sx={{ height: 20 }} />
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} Struxor
              </Typography>
            </Stack>
            <LegalBusinessDetails compact textAlign={location.pathname === '/login' || location.pathname === '/register' ? 'center' : 'left'} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent={{ xs: 'center', md: 'flex-end' }}>
            <Button size="small" color="inherit" component={RouterLink} to="/">Home</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/features">Features</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/pricing">Pricing</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/about">About</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/guide">Guide</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/contact">Contact</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/terms">Terms</Button>
            <Button size="small" color="inherit" component={RouterLink} to="/privacy">Privacy</Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
